import React, { useState, useEffect, useRef, useContext } from 'react';
import './Buscador.css';
import { useTranslation } from 'react-i18next';
import {
    getProductosPorBusquedaService,
    getCategoriasService,
    getProductosPorSKUService,
} from '../../services/Buscador';
import { globalContext } from '../Context/GlobalContext';
import { Button, Form, FormControl, InputGroup, Pagination } from 'react-bootstrap';
import { shouldShowMarca } from '../../utilities/marcasOcultas';

const normalizeText = (value = '') =>
    value
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

const Buscador = () => {
    const {
        searchInput,
        setSearchInput,
        suggestedProducts,
        setSuggestedProducts,
        suggestedBrands,
        setSuggestedBrands,
        suggestedCategories,
        setSuggestedCategories,
        categoria,
        setCategoria,
        suggestedSKUs,
        setSuggestedSKUs,
        categoriaLoaded,
        setCategoriaLoaded,
        userInteracted,
        setUserInteracted,
        closeSuggestions,
        handleSuggestionClick,
    } = useContext(globalContext);

    // eslint-disable-next-line no-unused-vars
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const searchInputRef = useRef(null);
    const [t] = useTranslation('global');
    const imgPlaceholder = `${process.env.PUBLIC_URL}/assets/sin-imagen.png`;
    const elementosPorPagina = 7;
    let debounceTimer;
    const [isSearchCompleted, setIsSearchCompleted] = useState(false);

    // Helper functions
    const fetchProductosPorBusqueda = async () => {
        setLoading(true);
        try {
            const productos = await getProductosPorBusquedaService(
                searchInput,
                elementosPorPagina,
                currentPage * elementosPorPagina
            );
            setSuggestedProducts(productos);
            localStorage.setItem('productos', JSON.stringify(productos));
        } catch (error) {
            setError('Failed to fetch products');
        } finally {
            setLoading(false);
            setIsSearchCompleted(true);
        }
    };

    const fetchCategorias = async (retryCount = 0) => {
        setLoading(true);
        try {
            const storedCategorias = localStorage.getItem('categorias-stored');
            if (storedCategorias) {
                setCategoria(JSON.parse(storedCategorias));
                return;
            }
            const categoriasData = await getCategoriasService();
            setCategoria(categoriasData);
        } catch (error) {
            if (retryCount < 3) {
                handleRetryFetching(error, retryCount);
            } else {
                console.error('Max retries reached, unable to fetch categories', error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRetryFetching = (error, retryCount) => {
        if (error?.response?.status === 500) {
            console.log('Server error.');
        } else if (error?.response?.status === 429) {
            console.log('Too many requests, retrying in 3 minutes.');
            setTimeout(() => fetchCategorias(retryCount + 1), 180000); // 3 minutes
        } else {
            setError(error);
            console.error('Fetch error:', error);
            setTimeout(() => fetchCategorias(retryCount + 1), 180000); // 3 minutes
        }
    };

    const fetchProductosPorSKU = async () => {
        setLoading(true);
        try {
            const skuData = await getProductosPorSKUService(searchInput);
            setSuggestedSKUs(skuData);
        } catch (error) {
            setError('Failed to fetch SKU data');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (direction) => {
        setCurrentPage((prevPage) => Math.max(prevPage + direction, 0));
    };

    // Effects
    useEffect(() => {
        if (!categoriaLoaded && categoria.length === 0 && !loading) {
            fetchCategorias();
            setCategoriaLoaded(true);
        }
    }, [categoria, categoriaLoaded, loading]);

    useEffect(() => {
        if (searchInput.length === 0) {
            setCurrentPage(0);
            setSuggestedProducts([]);
            setIsSearchCompleted(false);
            return;
        }
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            if (searchInput.length >= 3) {
                fetchProductosPorBusqueda();
                fetchProductosPorSKU();
                filterSuggestedCategories();
            }
        }, 300); // Debounce delay
        return () => clearTimeout(debounceTimer);
    }, [searchInput, currentPage]);

    useEffect(() => {
        const storedBrands = JSON.parse(localStorage.getItem('marca')) || [];
        const filteredBrands = storedBrands
            .filter(shouldShowMarca)
            .filter((brand) =>
                normalizeText(brand.Descripcion).includes(normalizeText(searchInput))
            );
        setSuggestedBrands(filteredBrands);
    }, [searchInput]);

    useEffect(() => {
        const handleInputFocus = () => {
            setUserInteracted(true);
        };

        searchInputRef.current.addEventListener('focus', handleInputFocus);

        return () => {
            searchInputRef.current.removeEventListener('focus', handleInputFocus);
        };
    }, []);

    useEffect(() => {
        document.addEventListener('click', closeSuggestions);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('click', closeSuggestions);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const handleKeyDown = (e) => {
        if (userInteracted && e.key === 'Escape') {
            closeSuggestions();
        }
    };

    const filterSuggestedCategories = () => {
        if (categoria) {
            const filteredCategories = categoria.filter((cat) =>
                cat.Descripcion.toLowerCase().includes(searchInput.toLowerCase())
            );
            setSuggestedCategories(filteredCategories);
        }
    };

    return (
        <Form className={`search-form ${searchInput ? 'active' : ''}`}>
            <div className="search-bar-container">
                <div className="search-input-container">
                    <InputGroup>
                        <FormControl
                            type="text"
                            className={`search-input form-control ${searchInput ? 'input-active' : ''}`}
                            placeholder={t('buscador.buscador1')}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={() => setUserInteracted(true)}
                            ref={searchInputRef}
                        />
                        <Button variant="outline-secondary" onClick={() => setSearchInput('')}>
                            <i className={`fas ${searchInput ? 'fa-times' : 'fa-search'}`}></i>
                        </Button>
                    </InputGroup>
                </div>
            </div>
            <div className={`suggested-results ${searchInput.length >= 2 ? 'show' : ''}`} onClick={(e) => e.stopPropagation()}>
                {searchInput.length >= 2 && (
                    <>

                        {renderSuggestedCategories()}
                        {renderSuggestedBrands()}
                        {renderSuggestedProducts()}
                        {renderSuggestedSKUs()}
                        {renderPagination()}
                    </>
                )}
                {isSearchCompleted && suggestedProducts.length === 0 && (
                    <h5 style={{ margin: '1em' }}>No se han encontrado productos que coincidan con su búsqueda.</h5>
                )}
            </div>
        </Form>
    );

    function renderSuggestedCategories() {
        if (suggestedCategories.length > 0) {
            return (
                <>
                    {suggestedCategories.map((category, index) => (
                        <div key={index} className="suggested-result" onClick={() => handleSuggestionClick(category.Codigo, 'categoria')}>
                            <div className="suggested-text flex-container">
                                <i className="fas fa-search icon-lupa"></i>
                                <span>{category.Descripcion} (Categoria)</span>
                            </div>
                        </div>
                    ))}
                    <hr className="divider" />
                </>
            );
        }
    }

    function renderSuggestedBrands() {
        if (suggestedBrands.length > 0) {
            return (
                <>
                    {suggestedBrands.map((brand, index) => (
                        <div key={index} className="suggested-result" onClick={() => handleSuggestionClick(brand.Codigo, 'marca')}>
                            <div className="suggested-text flex-container">
                                <i className="fas fa-search icon-lupa"></i>
                                <span>{brand.Descripcion} (Marca)</span>
                            </div>
                        </div>
                    ))}
                    <hr className="divider" />
                </>
            );
        }
    }

    function renderSuggestedProducts() {
        if (suggestedProducts.length > 0) {
            return (
                <>
                    <h6>Productos</h6>
                    {suggestedProducts.map((product, index) => (
                        <div key={index} className="suggested-result" onClick={() => handleSuggestionClick(product.Alias, 'producto')}>
                            <div className="suggested-text">
                                <img src={product.Imagen} alt="Product" className="imgThumb" loading="lazy" />
                                {product.Nombre}
                            </div>
                        </div>
                    ))}
                    <hr className="divider" />
                </>
            );
        }
    }

    function renderSuggestedSKUs() {
        if (suggestedSKUs.length > 0) {
            return (
                <>
                    {suggestedSKUs.map((sku, index) => (
                        <div key={index} className="suggested-result" onClick={() => handleSuggestionClick(sku.Alias, 'sku')}>
                            <div className="suggested-text">
                                <img src={sku.Imagen || imgPlaceholder} alt="SKU" className="imgThumb" loading="lazy" />
                                {sku.Nombre} (SKU)
                            </div>
                        </div>
                    ))}
                    <hr className="divider" />
                </>
            );
        }
    }

    function renderPagination() {
        if (suggestedProducts.length > 0) {
            return (
                <Pagination>
                    <Pagination.Prev onClick={() => handlePageChange(-1)} disabled={currentPage <= 0} />
                    <Pagination.Item>{currentPage + 1}</Pagination.Item>
                    <Pagination.Next onClick={() => handlePageChange(1)} disabled={suggestedProducts.length < elementosPorPagina} />
                </Pagination>
            );
        }
    }
};

export default Buscador;
